import { QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

/**
 * Domyślna liczba ponowień nieudanego zapytania (jeden retry przy network error, axios +
 * react-query) — jedno źródło dla klienta (`createQueryClient`) i dla zapytań, które własną regułą
 * ponawiania (`retryUnlessForbidden`) zastępują domyślną, a liczbę chcą zachować.
 */
export const DEFAULT_QUERY_RETRY = 1;

/** Czy błąd to odpowiedź 403 (brak uprawnienia) — ponowienie ani odświeżanie tego nie zmieni. */
export function isForbiddenError(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 403;
}

/** Czy błąd to odpowiedź 404 (zasób nie istnieje) — oczekiwany stan konfiguracji, nie awaria sieci ani serwera. */
export function isNotFoundError(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 404;
}

/**
 * Reguła `retry` zapytania: jak domyślna (`DEFAULT_QUERY_RETRY` ponowień), ale 403 nie jest
 * ponawiane. Własna funkcja `retry` zastępuje domyślną klienta w całości, stąd liczba stoi tu, a nie
 * jest przepisana w miejscu użycia.
 */
export function retryUnlessForbidden(failureCount: number, error: unknown): boolean {
  return !isForbiddenError(error) && failureCount < DEFAULT_QUERY_RETRY;
}

/**
 * QueryClient z defaultami dla całej apki (TF.3).
 *
 * - `staleTime: 60s` — dane świeże przez minutę, brak refetch przy każdym mount/focus.
 * - `retry: DEFAULT_QUERY_RETRY` (1) — jeden retry przy network error (axios + react-query).
 * - `refetchOnWindowFocus: false` — admin panel, nie potrzebujemy auto-refresh przy focus.
 *
 * F5/F8 może per-query nadpisać (np. liczniki dashboard `staleTime: 5s`).
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: DEFAULT_QUERY_RETRY,
        refetchOnWindowFocus: false,
        staleTime: 60_000,
      },
    },
  });
}
